import { useCallback, useEffect, useRef } from 'react'

export type HoldAccelerationTier = {
  /** Hold duration in ms at which this tier becomes active. */
  startsAt: number
  /** Step magnitude for this tier. */
  step: number
  /** Tick interval in ms while this tier is active. */
  intervalMs: number
}

export const HOLD_REPEAT_DELAY_MS = 150

/**
 * Money tiers are tuned so a sustained hold traverses 0 → $10M in ~2.5s.
 *
 *   Tier 0 (0–300ms):    $1,000 / 150ms — fine control, slow tap behaviour
 *   Tier 1 (300ms–1s):  $10,000 / 80ms  — kicks in immediately on hold
 *   Tier 2 (1s–2s):    $100,000 / 60ms
 *   Tier 3 (2s+):    $1,000,000 / 50ms — sweeps the full range
 */
export const MONEY_TIERS: readonly HoldAccelerationTier[] = [
  { startsAt: 0, step: 1_000, intervalMs: 150 },
  { startsAt: 300, step: 10_000, intervalMs: 80 },
  { startsAt: 1_000, step: 100_000, intervalMs: 60 },
  { startsAt: 2_000, step: 1_000_000, intervalMs: 50 },
]

/**
 * Age tiers are tuned so a sustained hold traverses 18 → 100 in ~2.5s.
 *
 *   Tier 0 (0–300ms):  1yr / 200ms — fine control near a target age
 *   Tier 1 (300ms–1s): 1yr /  60ms
 *   Tier 2 (1s–2s):    1yr /  30ms
 *   Tier 3 (2s+):      1yr /  15ms — full range sweep
 */
export const AGE_TIERS: readonly HoldAccelerationTier[] = [
  { startsAt: 0, step: 1, intervalMs: 200 },
  { startsAt: 300, step: 1, intervalMs: 60 },
  { startsAt: 1_000, step: 1, intervalMs: 30 },
  { startsAt: 2_000, step: 1, intervalMs: 15 },
]

const pickTier = (
  tiers: readonly HoldAccelerationTier[],
  elapsedMs: number
): HoldAccelerationTier => {
  let active = tiers[0]
  for (const tier of tiers) {
    if (tier.startsAt <= elapsedMs) active = tier
    else break
  }
  return active
}

/**
 * Money 1% cap: step never exceeds `round(value × 0.01 / 1000) × 1000`.
 * Floored at $1,000 so the smallest tier always advances even at low values.
 */
export const capMoneyStep =
  (value: number) =>
  (step: number): number => {
    const cap = Math.round((value * 0.01) / 1_000) * 1_000
    return Math.min(step, Math.max(1_000, cap))
  }

export type UseHoldAccelerationArgs = {
  tiers: readonly HoldAccelerationTier[]
  /** Optional per-tick cap on step magnitude (e.g. money 1% cap). */
  capStep?: (step: number) => number
  /** Apply a step. Return `false` if a boundary was hit so the hook stops. */
  onTick: (step: number) => boolean | void
}

export type UseHoldAccelerationResult = {
  /** Begin a hold: fires one tick immediately, then schedules repeats. */
  start: () => void
  /** End a hold: clears any pending repeat. Idempotent. */
  stop: () => void
}

/**
 * Schedules a sequence of `onTick` calls while the caller is "holding".
 *
 * - `start()` fires one tick immediately (tap semantics), then after
 *   `HOLD_REPEAT_DELAY_MS` begins repeating.
 * - Each repeating tick picks the tier whose `startsAt` is the largest
 *   `≤ elapsed`, applies `capStep` (if any), and calls `onTick(step)`.
 * - If `onTick` returns `false` (boundary hit), scheduling stops.
 * - `stop()` clears any pending timer.
 *
 * Uses chained `setTimeout` rather than `setInterval` because tier intervals
 * can change mid-hold (age tiers shrink).
 */
const useHoldAcceleration = ({
  tiers,
  capStep,
  onTick,
}: UseHoldAccelerationArgs): UseHoldAccelerationResult => {
  const tiersRef = useRef(tiers)
  const capStepRef = useRef(capStep)
  const onTickRef = useRef(onTick)
  const startTimeRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  tiersRef.current = tiers
  capStepRef.current = capStep
  onTickRef.current = onTick

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const fireTick = useCallback((step: number): boolean => {
    const capped = capStepRef.current ? capStepRef.current(step) : step
    return onTickRef.current(capped) !== false
  }, [])

  const scheduleNext = useCallback(
    (delayMs: number) => {
      clearTimer()
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        const elapsed = Date.now() - startTimeRef.current
        const tier = pickTier(tiersRef.current, elapsed)
        if (!fireTick(tier.step)) return
        scheduleNext(tier.intervalMs)
      }, delayMs)
    },
    [clearTimer, fireTick]
  )

  const start = useCallback(() => {
    clearTimer()
    startTimeRef.current = Date.now()
    if (!fireTick(tiersRef.current[0].step)) return
    scheduleNext(HOLD_REPEAT_DELAY_MS)
  }, [clearTimer, fireTick, scheduleNext])

  const stop = useCallback(() => {
    clearTimer()
  }, [clearTimer])

  useEffect(() => clearTimer, [clearTimer])

  return { start, stop }
}

export default useHoldAcceleration
